// Single source of truth for `URL → { area, feature, sub_feature? }`.
//
// Adding a new route? Add ONE entry here and page-view emits immediately.
// State-driven inner tabs (`useState`) still need a manual `analytics.track`
// call in the tab handler (see docs/analytics/README.md in the BE repo).

import type { AnalyticsArea } from './config'

export interface AnalyticsRouteResolution {
  area: AnalyticsArea
  feature: string
  subFeature?: string
}

interface AnalyticsRoute {
  match: RegExp
  resolve: (m: RegExpExecArray) => AnalyticsRouteResolution
}

// Order matters — first matching entry wins. More specific patterns come first.
const ROUTES: AnalyticsRoute[] = [
  // Workspace
  {
    match: /^\/workspace\/queue\/create\/?$/,
    resolve: () => ({ area: 'Chart Queue', feature: 'chart_queue', subFeature: 'new_chart' }),
  },
  {
    match: /^\/workspace\/queue\/dispatch(?:\/[^/]+)?\/?$/,
    resolve: () => ({ area: 'Chart Queue', feature: 'chart_queue', subFeature: 'dispatch' }),
  },
  {
    match: /^\/workspace\/queue\/[^/]+/,
    resolve: () => ({ area: 'Chart Details', feature: 'chart_details' }),
  },
  {
    match: /^\/workspace\/queue\/?$/,
    resolve: () => ({ area: 'Chart Queue', feature: 'chart_queue' }),
  },
  {
    match: /^\/workspace\/history\/[^/]+/,
    resolve: () => ({ area: 'Chart Details', feature: 'chart_details' }),
  },
  {
    match: /^\/workspace\/history\/?$/,
    resolve: () => ({ area: 'History', feature: 'history' }),
  },
  {
    match: /^\/workspace\/notes\/?$/,
    resolve: () => ({ area: 'Notes', feature: 'notes' }),
  },

  // Charts Dashboard (URL prefix: /client-admin)
  {
    match: /^\/client-admin\/([\w-]+)/,
    resolve: (m) => ({
      area: 'Charts Dashboard',
      feature: (m[1] ?? 'overview').replace(/-/g, '_'),
    }),
  },
  {
    match: /^\/client-admin\/?$/,
    resolve: () => ({ area: 'Charts Dashboard', feature: 'overview' }),
  },

  // Analytics Dashboard (URL prefix: /engine-admin)
  {
    match: /^\/engine-admin\/([\w-]+)/,
    resolve: (m) => ({
      area: 'Analytics Dashboard',
      feature: (m[1] ?? 'overview').replace(/-/g, '_'),
    }),
  },
  {
    match: /^\/engine-admin\/?$/,
    resolve: () => ({ area: 'Analytics Dashboard', feature: 'overview' }),
  },

  // Knowledge Base
  {
    match: /^\/kb\/([\w-]+)/,
    resolve: (m) => ({
      area: 'Knowledge Base',
      feature: (m[1] ?? 'overview').replace(/-/g, '_'),
    }),
  },
  {
    match: /^\/kb\/?$/,
    resolve: () => ({ area: 'Knowledge Base', feature: 'overview' }),
  },

  // Mandatory Fields (single-page; sub-tabs are state-driven)
  {
    match: /^\/platform-config\/mandatory-fields/,
    resolve: () => ({ area: 'Mandatory Fields', feature: 'mandatory_fields' }),
  },

  // Clients
  {
    match: /^\/clients\/[^/]+\/([\w-]+)/,
    resolve: (m) => ({ area: 'Clients', feature: 'details', subFeature: m[1] }),
  },
  {
    match: /^\/clients\/?$/,
    resolve: () => ({ area: 'Clients', feature: 'list' }),
  },

  // Access Requests
  {
    match: /^\/access\/new/,
    resolve: () => ({ area: 'Access Requests', feature: 'new_request' }),
  },
  {
    match: /^\/access\/requests\/[^/]+/,
    resolve: () => ({ area: 'Access Requests', feature: 'review_request' }),
  },
  {
    match: /^\/access(?:\/requests)?\/?$/,
    resolve: () => ({ area: 'Access Requests', feature: 'my_requests' }),
  },

  // Announcements
  {
    match: /^\/announcements\/timeline/,
    resolve: () => ({ area: 'Announcements', feature: 'timeline' }),
  },
  {
    match: /^\/announcements\/admin/,
    resolve: () => ({ area: 'Announcements', feature: 'admin' }),
  },
  {
    match: /^\/announcements\/?/,
    resolve: () => ({ area: 'Announcements', feature: 'hub' }),
  },

  // System Admin (platform-level, not tenant-scoped)
  {
    match: /^\/system-admin\/([\w-]+)/,
    resolve: (m) => ({
      area: 'System Admin',
      feature: (m[1] ?? 'overview').replace(/-/g, '_'),
    }),
  },
  {
    match: /^\/system-admin\/?$/,
    resolve: () => ({ area: 'System Admin', feature: 'overview' }),
  },

  // Operations Dashboard = the Batch runs dashboard (/batch-runs/*, see
  // features/batch-runs/routes.tsx). One entry per route shape; id segments
  // never reach the payload. Specific shapes first; the catch-all covers the
  // router's `*` redirect. Rows mirrored in t_sys_analytics_taxonomy
  // (migrations 20261029_002, 20261104_001). The Export, Batches and Charts tabs are
  // /batch-runs?work=export|batches|charts — a query param on the overview, so
  // they collapse into the overview row (no new route shape).
  ...(
    [
      [/^\/batch-runs\/facilities\/[^/]+\/?$/, 'facility_detail'],
      [/^\/batch-runs\/batches\/[^/]+\/?$/, 'batch_detail'],
      [/^\/batch-runs\/charts\/[^/]+\/?$/, 'chart_detail'],
      [/^\/batch-runs\/held\/?$/, 'held_charts'],
      [/^\/batch-runs\/failed-out\/?$/, 'failed_out_charts'],
      [/^\/batch-runs\/runs-today\/?$/, 'runs_today'],
    ] as const
  ).map(([match, subFeature]): AnalyticsRoute => ({
    match,
    resolve: () => ({ area: 'Operations Dashboard', feature: 'batch_runs', subFeature }),
  })),
  {
    match: /^\/batch-runs(?:\/|$)/,
    resolve: () => ({ area: 'Operations Dashboard', feature: 'batch_runs' }),
  },

  // Old Operations Dashboard URLs (now redirect to /batch-runs). Trail sub-pages get their own sub_feature so
  // analytics can tell a batch/chart drill-in apart from the import list.
  // Specific patterns come BEFORE the generic import matcher because
  // resolveRoute picks the first hit.
  {
    match: /^\/operations-dashboard\/import\/batches(?:\/[^/]+)?\/?$/,
    resolve: () => ({
      area: 'Operations Dashboard',
      feature: 'import',
      subFeature: 'batch_trail',
    }),
  },
  {
    match: /^\/operations-dashboard\/import\/charts(?:\/[^/]+)?\/?$/,
    resolve: () => ({
      area: 'Operations Dashboard',
      feature: 'import',
      subFeature: 'chart_trail',
    }),
  },
  {
    match: /^\/operations-dashboard\/([\w-]+)/,
    resolve: (m) => ({
      area: 'Operations Dashboard',
      feature: (m[1] ?? 'overview').replace(/-/g, '_'),
    }),
  },
  {
    match: /^\/operations-dashboard\/?$/,
    resolve: () => ({ area: 'Operations Dashboard', feature: 'overview' }),
  },

  // Prompt Library — URL is /prompt-config, sidebar label is "Prompt Library"
  {
    match: /^\/prompt-config\/([\w-]+)/,
    resolve: (m) => ({
      area: 'Prompt Library',
      feature: (m[1] ?? 'overview').replace(/-/g, '_'),
    }),
  },
  {
    match: /^\/prompt-config\/?$/,
    resolve: () => ({ area: 'Prompt Library', feature: 'overview' }),
  },

  // Client Config — feature = first segment (prediction / integration /
  // system-config), sub_feature = second segment when present so each tab
  // gets its own analytics row without needing state-driven track() calls.
  {
    match: /^\/client-config\/([\w-]+)\/([\w-]+)/,
    resolve: (m) => ({
      area: 'Client Config',
      feature: m[1].replace(/-/g, '_'),
      subFeature: m[2].replace(/-/g, '_'),
    }),
  },
  {
    match: /^\/client-config\/([\w-]+)\/?$/,
    resolve: (m) => ({
      area: 'Client Config',
      feature: (m[1] ?? 'overview').replace(/-/g, '_'),
    }),
  },
  {
    match: /^\/client-config\/?$/,
    resolve: () => ({ area: 'Client Config', feature: 'overview' }),
  },

  // Settings (profile / security / sessions / notifications)
  {
    match: /^\/settings\/([\w-]+)/,
    resolve: (m) => ({
      area: 'Settings',
      feature: (m[1] ?? 'profile').replace(/-/g, '_'),
    }),
  },
  {
    match: /^\/settings\/?$/,
    resolve: () => ({ area: 'Settings', feature: 'profile' }),
  },

  // Notifications — list + detail
  {
    match: /^\/notifications\/[^/]+/,
    resolve: () => ({ area: 'Notifications', feature: 'detail' }),
  },
  {
    match: /^\/notifications\/?$/,
    resolve: () => ({ area: 'Notifications', feature: 'list' }),
  },
]

/** Match a normalised pathname to the taxonomy. Returns null on no match. */
export function resolveRoute(path: string): AnalyticsRouteResolution | null {
  for (const r of ROUTES) {
    const m = r.match.exec(path)
    if (m) return r.resolve(m)
  }
  return null
}

/** Fallback used when `resolveRoute` returns null. */
export function fallbackResolution(path: string): AnalyticsRouteResolution {
  const first = path.split('/').filter(Boolean)[0] ?? 'unknown'
  return { area: 'Other', feature: first.replace(/-/g, '_') }
}
